<script>
/**
 * Overview
 * @version 1.0.0
 * @since 2.3.0
 */
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';

import {wizardListenerMixin} from '@/mixins/wizard';
import {LOADING_KEY} from '@/util/constants';
import {getCurrency} from '@/util/utils';

import EzEntityInfo from '@/components/ui/EntityInfo';
import EzButton from '@/components/ui/Button';
import VPrice from '@/components/v3/elements/VPrice';
import Paper from '@/components/layout/WizardOverviewPaper.vue';
import EzLoader from '@/components/ui/Loader/EzLoader';
import VTag from '@/components/v3/elements/VTag';

import flash from '@/components/ui/FlashMessage';
import {RECIPE_STEP_1, RECIPE_STEP_2} from './steps';

export default {
  mixins: [wizardListenerMixin],
  components: {
    EzEntityInfo,
    EzButton,
    EzLoader,
    VPrice,
    Paper,
    VTag,
  },
  data() {
    return {
      itemsListExpanded: false,
    };
  },
  computed: {
    ...mapState('recipes', ['draft']),
    ...mapState('tags', ['allTags']),
    ...mapGetters('loading', ['isSomeLoading']),
    ...mapGetters('defaultImages', ['getDefaultImage']),
    hasSalesPrice() {
      return this.draft.salesPrice !== null &&
        this.draft.salesPrice !== undefined &&
        this.draft.salesPrice !== '';
    },
    defaultImage() {
      return this.getDefaultImage('category');
    },
    isLoading() {
      return this.isSomeLoading([LOADING_KEY.VENUE_CREATE_RECIPE]);
    },
    products() {
      return this.draft.items;
    },
    listItems() {
      if (!this.itemsListExpanded) return this.products.slice(0, 8);
      return this.products;
    },
    expandButtonCopy() {
      return this.itemsListExpanded ? 'Collapse List' : 'Expand List';
    },
    expandButtonIcon() {
      return this.itemsListExpanded ? 'angle-up' : 'angle-down';
    },
    currency() {
      return getCurrency() || {};
    },
    isIngredient() {
      return this.draft.type === 'ingredient';
    },
    calculatedCogsAmount() {
      if (!this.draft.items || this.draft.items.length === 0) {
        return 0;
      }

      const amount = this.draft.items.reduce((total, item) => {
        if (!item.price || !item.quantity) return total;
        const itemCogs = this.isIngredient
          ? item.price / item.quantity
          : item.price * item.quantity;
        return total + itemCogs;
      }, 0);

      return this.isIngredient ? amount / this.draft.items.length : amount;
    },
    calculatedCogsPercent() {
      if (!this.draft.salesPrice || this.draft.salesPrice === 0 || !this.calculatedCogsAmount) {
        return 0;
      }

      return parseFloat(((this.calculatedCogsAmount / this.draft.salesPrice) * 100).toFixed(2)) || 0;
    },
    showCogsGrid() {
      const hasTargetAmount = this.draft.targetCogsAmount !== null
        && this.draft.targetCogsAmount !== undefined;
      const hasTargetPercent = this.draft.targetCogsPercent !== null
        && this.draft.targetCogsPercent !== undefined;
      const hasItems = this.draft.items && this.draft.items.length;
      return hasTargetAmount || hasTargetPercent || hasItems;
    },
  },
  methods: {
    ...mapActions('recipes', ['venueCreateRecipe', 'venueUpdateRecipe']),
    ...mapMutations('recipes', ['CLEAR_DRAFT']),
    getTagById(tagId) {
      return this.allTags.find(t => t.id === tagId) || null;
    },
    getTagName(tagId) {
      const tag = this.getTagById(tagId);
      return tag?.name || '';
    },
    getTagColor(tagId) {
      const tag = this.getTagById(tagId);
      return tag?.color?.colors || ['#000', '#f0f0f0'];
    },
    onPreviousStep() {
      this.$emit('stepBack');
    },
    buildRequestBody() {
      // If target COGS fields are empty, auto-fill with calculated values from Step 2
      const finalTargetCogsAmount =
        (this.draft.targetCogsAmount !== null && this.draft.targetCogsAmount !== undefined)
          ? this.draft.targetCogsAmount
          : this.calculatedCogsAmount;

      const finalTargetCogsPercent =
        (this.draft.targetCogsPercent !== null && this.draft.targetCogsPercent !== undefined)
          ? this.draft.targetCogsPercent
          : this.calculatedCogsPercent;

      return {
        ...this.draft,
        quantity: this.draft.quantity || '',
        salesPrice: this.draft.salesPrice || null,
        inventoryTracked: this.draft.inventoryTracked || false,
        recipeItems: this.draft.items.map(({id, ...item}) => item),
        targetCogsAmount: finalTargetCogsAmount,
        targetCogsPercent: finalTargetCogsPercent,
      };
    },
    appendPrimitiveFields(formData, body) {
      const allowedFields = [
        'name',
        'unitId',
        'type',
        'quantity',
        'salesPrice',
        'inventoryTracked',
        'id',
        'visibleForVenueIdsFromGroup',
        'showVisibility',
        'currentCogsAmount',
        'targetCogsAmount',
        'targetCogsPercent',
      ];

      allowedFields.forEach(key => {
        if (body[key] !== undefined) {
          formData.append(key, body[key]);
        }
      });
    },
    appendImage(formData) {
      if (!this.draft.imgModified) return;

      if (this.draft.image) {
        formData.append(
          'image',
          new File([this.draft.image], 'recipe-image.png', {type: 'image/png'}),
        );
      } else {
        formData.append('image', '');
      }
    },
    appendRecipeItems(formData, recipeItems) {
      recipeItems.forEach((item, index) => {
        Object.entries(item).forEach(([key, value]) => {
          if (value !== undefined) {
            formData.append(`recipeItems[${index}][${key}]`, value);
          }
        });
      });
    },
    appendTagIds(formData, tagIds) {
      if (!Array.isArray(tagIds) || !tagIds.length) return;

      tagIds.forEach((tagId, index) => {
        formData.append(`tagIds[${index}]`, tagId);
      });
    },
    buildFormData() {
      const body = this.buildRequestBody();
      const formData = new FormData();

      this.appendPrimitiveFields(formData, body);
      this.appendImage(formData);
      this.appendRecipeItems(formData, body.recipeItems);
      this.appendTagIds(formData, body.tagIds);

      return formData;
    },
    async submitRecipe(formData) {
      const action = this.draft.id ? 'venueUpdateRecipe' : 'venueCreateRecipe';
      const params = this.draft.id ? {body: formData, id: this.draft.id} : {body: formData};
      const successMessage = this.draft.id ? 'Recipe successfully updated!' : 'Recipe successfully created!';

      await this[action](params);
      flash.success({title: successMessage});
    },
    async onNextStep() {
      try {
        const formData = this.buildFormData();
        await this.submitRecipe(formData);
        this.CLEAR_DRAFT();
        this.$router.push({name: 'venue-recipes'});
      } catch (e) {
        flash.error({
          title: 'Something went wrong',
          message: e.response?.data?.error?.message || '',
        });
      }
    },
    editInfo() {
      this.$emit('stepBack', RECIPE_STEP_1);
    },
    editProducts() {
      this.$emit('stepBack', RECIPE_STEP_2);
    },
    toggleListExpanded() {
      this.itemsListExpanded = !this.itemsListExpanded;
    },
  },
  async created() {
    this.enableNextStep();
  },
};
</script>

<template>
  <div class="create-order">
    <div class="create-order__main-content">
      <paper class="venue-section">
        <template #actions>
          <ez-button type="link" formType="button" @click="editInfo">
            <font-awesome-icon icon="pen"/>
            <span>Edit</span>
          </ez-button>
        </template>
        <ez-entity-info
          :imgUrl="draft.image || defaultImage"
          imgHeight="64px"
          imgWidth="64px"
          imageBorderRadius="50%"
          imageHasBorder
        >
          <template>
            <span>
              {{ draft?.name }}
            </span>
          </template>
        </ez-entity-info>
        <hr/>
        <div class="info info--vertical">
          <small>Type</small>
          <span>{{ draft.typeObject?.name }}</span>
        </div>
        <div class="info info--vertical">
          <small>Unit</small>
          <span>{{ draft.unit?.name }}</span>
        </div>
        <div v-if="hasSalesPrice" class="info info--vertical cogs-item">
          <small>Sales Price</small>
          <v-price :price="draft.salesPrice" class="cogs-item__value"/>
        </div>
        <div v-if="showCogsGrid" class="cogs-grid">
          <div
            v-if="draft.targetCogsAmount !== null && draft.targetCogsAmount !== undefined"
            class="cogs-item"
          >
            <small class="cogs-item__label">Target COGS($)</small>
            <v-price :price="draft.targetCogsAmount" class="cogs-item__value"/>
          </div>
          <div
            v-if="draft.targetCogsPercent !== null && draft.targetCogsPercent !== undefined && hasSalesPrice"
            class="cogs-item"
          >
            <small class="cogs-item__label">Target COGS(%)</small>
            <span class="cogs-item__value">{{ draft.targetCogsPercent }}%</span>
          </div>
          <div v-if="draft.items && draft.items.length" class="cogs-item">
            <small class="cogs-item__label">Calculated COGS($)</small>
            <v-price :price="calculatedCogsAmount" class="cogs-item__value"/>
          </div>
          <div v-if="hasSalesPrice && draft.items && draft.items.length" class="cogs-item">
            <small class="cogs-item__label">Calculated COGS(%)</small>
            <span class="cogs-item__value">{{ calculatedCogsPercent }}%</span>
          </div>
        </div>
        <div class="info info--vertical">
          <small>Inventory Tracked</small>
          <span>{{ draft.inventoryTracked ? 'Yes' : 'No' }}</span>
        </div>
        <div v-if="draft.tagIds && draft.tagIds.length" class="info info--vertical">
          <small>Tags</small>
          <div class="tags-display">
            <v-tag
              v-for="tagId in draft.tagIds"
              :key="tagId"
              :color="getTagColor(tagId)[0]"
              :backgroundColor="getTagColor(tagId)[1]"
            >
              {{ getTagName(tagId) }}
            </v-tag>
          </div>
        </div>
      </paper>

      <paper>
        <template #title>Products ({{ products.length }})</template>
        <template #actions>
          <ez-button type="link" formType="button" @click="editProducts">
            <font-awesome-icon icon="pen"/>
            <span>Edit</span>
          </ez-button>
        </template>
        <div class="products-table--mobile">
          <div
            v-for="item in listItems"
            :key="item.id"
            class="product-item"
            :id="`product-item-${item.id}`"
          >
            <div class="first-row">
              <ez-entity-info imgWidth="2.5rem" imgHeight="2.5rem" :imgUrl="item.image">
                <div class="product-info mr-8" :title="item?.name">
                  <span>
                    {{ item?.name }}
                    <span v-if="item.distributor && item.distributor.name">
                      ({{ item.distributor.name }})
                    </span>
                  </span>
                  <span class="product-info-secondary">
                    <span class="product-info-secondary--category category-badge">
                      {{ item.unit.label }}
                    </span>
                  </span>
                </div>
              </ez-entity-info>

            </div>
            <hr/>
            <div class="second-row">
              <div class="quantity-input">
                <span>{{ item.quantity }} </span>
                <span class="unit-label" v-if="!isIngredient">{{ item.unit?.label }}</span>
                <span class="unit-label" v-else>{{ draft.unit?.label }}</span>
              </div>
              <v-price
                v-if="!isIngredient"
                :price="item.price * item.quantity || 0"
                :unit="(draft.unit || {})?.label"
                :currency="item.currency || currency || {}"
                flexRow
              />
              <v-price
                v-else
                :price="item.price / item.quantity || 0"
                :unit="(draft.unit || {})?.label"
                :currency="item.currency || currency || {}"
                flexRow
              />
            </div>
          </div>
        </div>
        <template #footer>
          <div v-if="draft.items.length > 8" class="u-flex-h-center mt-16">
            <ez-button @click="toggleListExpanded" type="link" formType="button">
              <span>{{ expandButtonCopy }}</span>
              <font-awesome-icon class="ml-8" :icon="expandButtonIcon"/>
            </ez-button>
          </div>
        </template>
      </paper>
      <ez-loader :show="isLoading">Loading...</ez-loader>
    </div>
  </div>
</template>

<style scoped lang="scss">
.create-order {
  width: 100%;
  display: flex;
  @media (max-width: 768px) {
    flex-direction: column;
  }

  &__main-content {
    width: 100%;
  }
}

.venue-section :deep() {
  .overview-paper__content {
    padding-bottom: 12px;
  }

  .entity-info {
    flex-direction: column;
    align-items: center;
    margin-right: 24px;

    &__text {
      justify-content: center;
      margin-left: 0;
      margin-top: 12px;
      @include font-size(16px, 22px);
      font-weight: 600;
      color: $color-gray-25;
    }
  }

  .ref-number {
    &__label {
      @include font-size(12px, 18px);
      color: $color-gray-6C;
    }

    &__value {
      @include font-size(14px, 20px);
      color: $color-gray-25;
    }
  }
}

.products-table {
  .quantity-cell {
    width: 64px + $spacing-32;

    .quantity-input {
      :deep() .input-group {
        input {
          color: $color-primary-blue;
          font-weight: bold;
        }

        button {
          display: none;
        }
      }
    }
  }

  .pricePerUnit-cell {
    width: 90px + $spacing-32;

    :deep() .v-price__price {
      font-weight: bold;

      &:not(.v-price__market-price--row, .v-price__unit--row) {
        color: $color-gray-25;
      }
    }
  }

  .discount-cell {
    width: 95px + $spacing-32;
    overflow: visible;

    :deep() {
      .mask-input {
        &__input {
          @include font-size(16px, 20px);
        }

        &__prefix {
          color: $color-gray-6C;
          font-weight: bold;
        }
      }

      .mark-input__error {
        display: none;
      }
    }
  }

  .total-cell {
    width: 125px;
    overflow: visible;

    :deep() span {
      font-weight: bold;
      color: $color-gray-25;
    }
  }

  .product-cell {
    .product-info {
      display: flex;
      flex-direction: column;

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      &__unit {
        color: $color-gray-25;
      }
    }
  }

  tbody tr.item-bonus {
    background-color: $color-pastel-blue;

    td:first-child {
      padding-left: 8px;
    }

    td:last-child {
      padding-right: 8px;
    }

    :deep() .has-border {
      border: 1px solid $color-primary-blue;
    }

    .additional-info--bonus {
      color: $color-gray-6C;
      font-size: 12px;
      font-weight: 500;
      line-height: 14px;
      border-bottom: 2px dotted #a3bbff;
      color: $color-primary-blue;
    }
  }
}

.note-options {
  .note-option {
    &__label {
      @include font-size(12px, 22px);
      font-weight: 600;
      color: $color-gray-25;
    }
  }
}

.info-icon {
  width: $spacing-16;
  height: $spacing-16;
}

:deep() .loader {
  @extend %whole-screen;
}

:deep() .mask-input-wrapper {
  .mask-input__input {
    text-align: center;
  }
}

.ez-info-panel {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: -28px;
  align-items: center;

  &__title {
    @extend %flex-center;
    margin-bottom: 1.25rem;

    h3 {
      @include font-size(20px);
      font-weight: 500;
      margin: 0;
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;

    small {
      @extend %gray-label;
      font-weight: 500;
      margin-bottom: 0.5rem;
    }

    span {
      @include font-size(14px);
      white-space: pre-line;
    }

    a {
      color: #4d7cfe;
    }
  }

  &__contact {
    display: flex;
    margin-top: 1rem;

    &:not(:last-child) {
      padding-bottom: 0.5rem;
    }
  }

  &__close-button {
    margin-left: auto;
    cursor: pointer;

    svg {
      fill: $color-gray-6C;
    }
  }

  .venue-info {
    margin-top: -16px;
  }
}

.products-table--mobile {
  padding-bottom: 32px;
  width: 100%;

  .product-item {
    padding: 12px 0;
    border-bottom: 1px solid #e1e5ed;

    hr {
      margin: 12px 0;
    }

    &:hover {
      cursor: pointer;
    }
  }

  .first-row {
    display: flex;
    align-items: center;
    line-height: 1.5;
    justify-content: space-between;

    .supplier-name {
      color: $color-gray-25;
      font-size: 12px;
      line-height: 1.5;
      white-space: nowrap;
      margin-left: 8px;
      flex-shrink: 0;
    }

    .entity-info {
      align-items: center;
      min-width: 0;
      flex-shrink: 1;
    }

    .product-quantity {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      color: $color-gray-6C;
      font-size: 14px;
      flex-shrink: 0;

      .product-info-secondary--category {
        font-size: 12px;
      }
    }
  }

  .second-row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .quantity__icon {
      padding-left: 4px;
    }

    .quantity-input {
      gap: 4px;
      display: flex;
      align-items: center;

      :deep() .mask-input-wrapper {
        max-width: 100px;
      }

      :deep() .mask-input-wrapper .mask-input .mask-input__input {
        border-radius: 0;
        font-size: 16px;
      }
    }

    .quantity .product-card-cart {
      display: flex;
      align-items: center;
      width: 120px;
      margin-right: 12px;

      :deep() .mask-input-wrapper {
        margin: 0 8px;
      }

      :deep() .mask-input-wrapper .mask-input .mask-input__input {
        border-radius: 0;
        font-size: 16px;
      }
    }
  }

  .product-info {
    display: flex;
    flex-direction: column;
    line-height: 1.5;
    font-size: 14px;
    font-weight: 500;

    .product-info-secondary {
      display: block;
      font-size: 12px;
    }

    span {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}

@media (max-width: 768px) {
  .products-table--mobile {
    display: block;
  }
  :deep() .products-table {
    display: none;
  }
}

.tags-display {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.info .tags-display span {
  font-size: 0.6875rem !important;
}

.cogs-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 16px 0;
}

.cogs-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;

  &__label {
    font-size: 12px;
    line-height: 18px;
    color: $color-gray-6C;
    font-weight: 500;
    margin: 0;
  }

  &__value {
    font-size: 1rem;
    line-height: 22px;
    color: $color-gray-25;
    font-weight: bold;
  }
}
</style>
